import { CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useReducer, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { Chip, pluralize } from "../../helpers/general";
import { useActivitiesQuery } from "../../hooks/Activities/useActivitiesQuery";
import { useAuth } from "../../hooks/useAuth";
import { useQueryParam } from "../../hooks/useQueryParam";
import { general } from "../../locales/general";
import ActivitiesTable from "./activityTable";
import { cardStyle } from "../Places/PlacesTable";
import { controlStyle } from "../../helpers/constants";

export default function Activities() {
  const { isAdmin } = useAuth();
  const { add, addMultiple } = useQueryParam();
  const [searchParams] = useSearchParams();

  const {
    Activities,
    fetchingActivities,
    deleteActivity,
    mutating,
    totalActivities,
    totalPages,
    currentPage,
  } = useActivitiesQuery();

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  // Local input state, the URL is updated once after a short pause
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") ?? "",
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      const current = searchParams.get("search") ?? "";
      const next = searchInput.toLowerCase();
      if (next !== current) addMultiple({ search: next, page: "1" });
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      deleteActivity(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = totalActivities || 0;

  return (
    <section>
      {/* Title + count + add */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fs-4 fw-bold mb-0">Activités</h5>
          <Chip tone="warning">
            {total} {pluralize("Activité", total)}
          </Chip>
        </div>

        <Link
          to="/activities/new"
          className="btn btn-primary text-white rounded-pill px-4"
        >
          + Ajouter une activité
        </Link>
      </div>

      {/* Toolbar */}
      <div
        style={{ ...cardStyle, padding: "12px 16px" }}
        className="d-flex flex-wrap align-items-center gap-2 mb-3"
      >
        <div
          className="d-flex align-items-center gap-2 px-3 flex-grow-1"
          style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
        >
          <MagnifyingGlassIcon color="gray" width={18} />
          <input
            type="text"
            placeholder="Rechercher une activité"
            className="border-0 w-100 bg-transparent"
            style={{ outline: "none", fontSize: 13 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        {isAdmin && (
          <Link
            to="/activities/organize"
            className="btn btn-outline-primary rounded-pill px-3 d-flex align-items-center"
            style={{ height: 38 }}
          >
            Organiser les activités
          </Link>
        )}

        <button
          type="button"
          disabled={mutating || selectedCount === 0}
          onClick={handleBulkDelete}
          className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
        >
          {mutating ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            <>
              <DeleteIcon fill={colors.primary} /> Supprimer
              {selectedCount > 0 && ` (${selectedCount})`}
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={fetchingActivities}
          render={() =>
            Activities?.length > 0 ? (
              <ActivitiesTable
                activities={Activities}
                onDeleteSelect={dispatch}
              />
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={Number(currentPage) || 1}
        onPageChange={(page) => add("page", String(page))}
        siblingCount={1}
        totalCount={totalPages}
        pageSize={10}
      />
    </section>
  );
}
